import type { LandscapeGoal, LandscapeSystem } from '../../types/landscape'

// The infographic: your systems on the left, Scout in the middle, the firm's goals on the right.
// Hand-drawn SVG in skin tokens (no icon font, no hex); every node's state comes from the API.
// Clicking a system with a route goes there; the "+" at the foot of the goals column adds a goal.

const ICONS: Record<string, JSX.Element> = {
  mailbox: (
    <>
      <rect x={2} y={5} width={20} height={14} rx={2} />
      <path d="M2 7l10 7 10-7" />
    </>
  ),
  crm: (
    <>
      <circle cx={8} cy={8} r={3.2} />
      <circle cx={17} cy={9} r={2.6} />
      <path d="M2.5 19c0-3.3 2.5-5.5 5.5-5.5s5.5 2.2 5.5 5.5M13.5 18c.3-2.4 1.8-4 3.8-4 2.2 0 4 1.8 4 4.2" />
    </>
  ),
  calendar: (
    <>
      <rect x={3} y={5} width={18} height={16} rx={2} />
      <path d="M3 10h18M8 3v4M16 3v4" />
      <rect x={7} y={13} width={3} height={3} rx={0.5} />
    </>
  ),
  onenote: (
    <>
      <rect x={6} y={3} width={14} height={18} rx={2} />
      <path d="M4 7h4M4 12h4M4 17h4M10 9h6M10 13h6M10 17h3" />
    </>
  ),
  warehouse: (
    <>
      <ellipse cx={12} cy={6} rx={8} ry={3} />
      <path d="M4 6v12c0 1.7 3.6 3 8 3s8-1.3 8-3V6M4 12c0 1.7 3.6 3 8 3s8-1.3 8-3" />
    </>
  ),
  signals: (
    <>
      <circle cx={12} cy={12} r={9} />
      <circle cx={12} cy={12} r={4.5} />
      <circle cx={12} cy={12} r={1} />
      <path d="M12 12l6-6" />
    </>
  ),
  imports: (
    <>
      <path d="M12 16V4M7 9l5-5 5 5" />
      <path d="M4 16v3a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-3" />
    </>
  ),
}

const GOAL_ICONS: Record<string, JSX.Element> = {
  originations: <path d="M3 20h18M6 20V11l4-3v12M14 20V6l4-2v16" />,
  activity: <path d="M4 12h3l2-6 3 12 3-9 2 3h3" />,
  voice: <path d="M12 4a3 3 0 013 3v5a3 3 0 01-6 0V7a3 3 0 013-3zM6 11a6 6 0 0012 0M12 17v3M9 20h6" />,
  'hunting-list': <path d="M4 6h10M4 12h10M4 18h7M17 15l2 2 4-4" />,
  'follow-through': <path d="M5 12l4 4L19 6M5 18h14" />,
  signals: (
    <>
      <circle cx={12} cy={12} r={9} />
      <circle cx={12} cy={12} r={1.5} />
      <path d="M12 12l6-6" />
    </>
  ),
  coordination: (
    <>
      <circle cx={7} cy={8} r={3} />
      <circle cx={17} cy={8} r={3} />
      <path d="M2 20c0-3 2.2-5 5-5s5 2 5 5M12 20c0-3 2.2-5 5-5s5 2 5 5" />
    </>
  ),
  'weekly-update': <path d="M4 5h16v11H4zM8 20h8M12 16v4M7 12l3-3 3 2 4-4" />,
  custom: <path d="M12 3l2.6 5.6 6.1.7-4.5 4.2 1.2 6L12 16.6 6.6 19.5l1.2-6L3.3 9.3l6.1-.7z" />,
}

export function LandscapeMap({
  systems, goals, onOpen, onAddGoal, onRemoveGoal,
}: {
  systems: LandscapeSystem[]
  goals: LandscapeGoal[]
  onOpen: (route: string) => void
  onAddGoal: () => void
  onRemoveGoal: (id: string) => void
}) {
  const W = 1080
  const rowH = 78
  const rows = Math.max(systems.length, goals.length + 1)
  const H = rows * rowH + 56
  const leftX = 48
  const hubX = W / 2
  const rightX = W - 48
  const hubY = H / 2 + 8
  const nodeW = 250
  const nodeH = 62

  const rowY = (i: number) => 56 + i * rowH + nodeH / 2
  // "connected" for THIS person: a firm system the firm connected, a personal system they enrolled themselves
  const connected = new Set(systems.filter((s) => (s.scope === 'firm' ? s.status === 'connected' : s.mine)).map((s) => s.id))
  const systemSub = (s: LandscapeSystem): string => {
    if (s.status === 'planned') return `${s.kind} · coming${s.scope === 'firm' && !s.canConnect ? ' · admin-only' : ''}`
    if (s.scope === 'personal') {
      const firm = s.firmCount === 0 ? 'none yet' : `${s.firmCount} connected`
      return `${firm} · yours ${s.mine ? 'connected' : s.canConnect ? 'not yet' : 'n/a'}`
    }
    return `${s.kind} · ${s.status === 'connected' ? 'connected' : s.canConnect ? 'connect now' : 'admins connect'}`
  }
  const sysName = (id: string) => systems.find((s) => s.id === id)?.name.toLowerCase() ?? id

  // wrap a title onto two lines when it will not fit the node (≈ 7.2 px per character at 13 px bold)
  const textX = 46
  const wrap = (title: string): string[] => {
    const max = Math.floor((nodeW - textX - 14) / 7.2)
    if (title.length <= max) return [title]
    const words = title.split(' ')
    const first: string[] = []
    while (words.length && (first.join(' ') + ' ' + words[0]).trim().length <= max) first.push(words.shift()!)
    let rest = words.join(' ')
    if (rest.length > max) rest = rest.slice(0, max - 1).trimEnd() + '…'
    return [first.join(' '), rest]
  }
  const NodeText = ({ title, sub }: { title: string; sub: string }) => {
    const lines = wrap(title)
    const two = lines.length > 1
    return (
      <>
        <text x={textX} y={two ? 19 : 25} className="map-node-title">
          {lines.map((l, i) => (
            <tspan key={i} x={textX} dy={i === 0 ? 0 : 15}>{l}</tspan>
          ))}
        </text>
        <text x={textX} y={two ? 50 : 45} className="map-node-sub">{sub}</text>
      </>
    )
  }
  const Icon = ({ glyph }: { glyph: JSX.Element }) => <g className="map-icon" transform="translate(13 19)">{glyph}</g>
  const Check = ({ x }: { x: number }) => (
    <g className="map-check" transform={`translate(${x} 18)`}>
      <circle r={8} />
      <path d="M-3.5 0l2.5 2.5 4.5-5" />
    </g>
  )

  return (
    <svg className="landscape-map" viewBox={`0 0 ${W} ${H}`} role="img" aria-label="What Scout does: connect your systems, Scout learns and watches, the firm reaches its goals">
      <defs>
        <pattern id="dots" width={18} height={18} patternUnits="userSpaceOnUse">
          <circle cx={1} cy={1} r={1} className="map-dotgrid" />
        </pattern>
        <radialGradient id="hub-glow" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="var(--brand)" stopOpacity={0.22} />
          <stop offset="100%" stopColor="var(--brand)" stopOpacity={0} />
        </radialGradient>
        <linearGradient id="hub-fill" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="var(--ink)" />
          <stop offset="100%" stopColor="var(--brand-strong)" />
        </linearGradient>
        <marker id="arrow" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
          <path d="M 0 0 L 10 5 L 0 10 z" fill="var(--line)" />
        </marker>
        <marker id="arrow-on" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
          <path d="M 0 0 L 10 5 L 0 10 z" fill="var(--brand)" />
        </marker>
      </defs>

      <rect x={0} y={0} width={W} height={H} fill="url(#dots)" />
      <circle cx={hubX} cy={hubY} r={230} fill="url(#hub-glow)" />

      <g className="map-col">
        <text x={leftX} y={26}>Your systems</text>
        <line x1={leftX} y1={33} x2={leftX + 40} y2={33} />
        <text x={hubX} y={26} textAnchor="middle">Scout</text>
        <line x1={hubX - 20} y1={33} x2={hubX + 20} y2={33} />
        <text x={rightX} y={26} textAnchor="end">Your goals</text>
        <line x1={rightX - 40} y1={33} x2={rightX} y2={33} />
      </g>

      {/* links: systems → hub, hub → goals */}
      {systems.map((s, i) => {
        const y = rowY(i)
        const on = s.status === 'connected'
        const d = `M ${leftX + nodeW} ${y} C ${leftX + nodeW + 110} ${y}, ${hubX - 190} ${hubY}, ${hubX - 118} ${hubY}`
        return (
          <g key={s.id}>
            <path d={d} className={on ? 'map-link on' : 'map-link'} markerEnd={on ? 'url(#arrow-on)' : 'url(#arrow)'} />
            {on && <path d={d} className="map-link flow" />}
          </g>
        )
      })}
      {goals.map((g, i) => {
        const y = rowY(i)
        const d = `M ${hubX + 118} ${hubY} C ${hubX + 190} ${hubY}, ${rightX - nodeW - 110} ${y}, ${rightX - nodeW} ${y}`
        return (
          <g key={g.id}>
            <path d={d} className={g.unlocked ? 'map-link on' : 'map-link'} markerEnd={g.unlocked ? 'url(#arrow-on)' : 'url(#arrow)'} />
            {g.unlocked && <path d={d} className="map-link flow" />}
          </g>
        )
      })}

      {/* the hub */}
      <g transform={`translate(${hubX} ${hubY})`} className="map-hubgroup">
        <circle r={114} className="map-hub-orbit slow" />
        <circle r={100} className="map-hub-orbit fast" />
        <circle r={84} fill="url(#hub-fill)" className="map-hub" />
        <circle r={84} className="map-hub-rim" />
        <g className="map-hub-mark" transform="translate(0 -46)">
          <circle r={7} />
          <circle r={3.5} />
          <path d="M0 0l5-5" />
        </g>
        <text y={-14} textAnchor="middle" className="map-hub-title">Scout</text>
        <text y={8} textAnchor="middle" className="map-hub-sub">learns your voice</text>
        <text y={23} textAnchor="middle" className="map-hub-sub">maps relationships</text>
        <text y={38} textAnchor="middle" className="map-hub-sub">watches for signals</text>
      </g>

      {/* systems */}
      {systems.map((s, i) => {
        const y = rowY(i) - nodeH / 2
        const stateCls = s.status === 'planned' ? 'planned' : (s.scope === 'firm' ? s.status === 'connected' : s.mine) ? 'connected' : 'available'
        const cls = `map-node ${stateCls}${s.route && s.canConnect ? ' clickable' : ''}`
        return (
          <g key={s.id} transform={`translate(${leftX} ${y})`} className={cls} onClick={() => s.route && s.canConnect && onOpen(s.route)} role={s.route && s.canConnect ? 'button' : undefined} tabIndex={s.route && s.canConnect ? 0 : undefined}
            onKeyDown={(e) => { if (s.route && s.canConnect && (e.key === 'Enter' || e.key === ' ')) onOpen(s.route) }}>
            <rect width={nodeW} height={nodeH} rx={10} className="map-node-shadow" />
            <rect width={nodeW} height={nodeH} rx={10} />
            <Icon glyph={ICONS[s.id] ?? ICONS.signals} />
            <NodeText title={s.name} sub={systemSub(s)} />
            <g className="map-badge" transform="translate(-2 -2)">
              <circle r={11} />
              <text textAnchor="middle" dy={4}>{i + 1}</text>
            </g>
            {(s.scope === 'firm' ? s.status === 'connected' : s.mine) && <Check x={nodeW - 20} />}
            {s.canConnect && s.status !== 'planned' && !(s.scope === 'firm' ? s.status === 'connected' : s.mine) && <circle cx={nodeW - 18} cy={16} r={4} className="map-pulse" />}
            {s.scope === 'firm' && <text x={nodeW - 12} y={13} textAnchor="end" className="map-scope">firm</text>}
          </g>
        )
      })}

      {/* goals */}
      {goals.map((g, i) => {
        const y = rowY(i) - nodeH / 2
        const needs = g.dependsOn.filter((d) => !connected.has(d)).map(sysName)
        const sub = g.unlocked ? 'unlocked' : g.dependsOn.length === 0 ? 'no system needed' : `needs ${needs.join(' + ')}`
        return (
          <g key={g.id} transform={`translate(${rightX - nodeW} ${y})`} className={`map-node goal ${g.unlocked ? 'connected' : 'planned'}${g.custom ? ' custom' : ''}`}>
            <rect width={nodeW} height={nodeH} rx={10} className="map-node-shadow" />
            <rect width={nodeW} height={nodeH} rx={10} />
            <Icon glyph={GOAL_ICONS[g.custom ? 'custom' : g.id] ?? GOAL_ICONS.custom} />
            <NodeText title={g.title} sub={sub} />
            {g.unlocked && !g.custom && (
              <g className="map-check" transform={`translate(${nodeW - 20} ${nodeH - 16})`}>
                <circle r={8} />
                <path d="M-3.5 0l2.5 2.5 4.5-5" />
              </g>
            )}
            {g.custom && (
              <g className="map-remove" transform={`translate(${nodeW - 18} 16)`} role="button" tabIndex={0} aria-label={`Remove goal ${g.title}`}
                onClick={(e) => { e.stopPropagation(); onRemoveGoal(g.id) }}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.stopPropagation(); onRemoveGoal(g.id) } }}>
                <circle r={8} />
                <path d="M-3 -3l6 6M3 -3l-6 6" />
              </g>
            )}
          </g>
        )
      })}

      {/* add a goal */}
      <g transform={`translate(${rightX - nodeW} ${rowY(goals.length) - nodeH / 2})`} className="map-node add" role="button" tabIndex={0} aria-label="Add a goal"
        onClick={onAddGoal} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onAddGoal() }}>
        <rect width={nodeW} height={nodeH} rx={10} />
        <g className="map-icon" transform="translate(13 19)">
          <path d="M12 5v14M5 12h14" />
        </g>
        <text x={textX} y={25} className="map-node-title">Add a goal</text>
        <text x={textX} y={45} className="map-node-sub">what else should Scout deliver?</text>
      </g>
    </svg>
  )
}
